import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { DialogConfirm, DialogConfirmData } from 'app/dialogs/confirm/dialog-confirm';
import { formatDate } from 'app/helpers/formatDate';
import { InlineBase64UploadAdapter } from 'app/helpers/inlineBase64UploadAdapter';
import { sortCompare } from 'app/helpers/sortCompare';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListJiraIssuesReq } from 'app/interfaces/requests/Exposed/exposedListJiraIssuesReq';
import { ExposedListDWAssistsJiraStsRes } from 'app/interfaces/responses/Exposed/exposedListDWAssistsJiraStsRes';
import { ExposedListPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryPersonsRes';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { JiraCreateIssueDTOReq } from 'app/interfaces/support/jira/jiraCreateIssueDTOReq';
import { JiraCreateIssueFieldsDescriptionDTOs } from 'app/interfaces/support/jira/jiraCreateIssueFieldsDescriptionDTOs';
import { JiraIssuesListRes } from 'app/interfaces/support/jira/jiraIssuesListRes';
import { JiraService } from 'app/services/api/jira/jira.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { HtmlToAdfService } from 'app/services/system/htmltoadf/htmltoadf.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { GrantsTypes } from 'common/constants/assists-jira-ea.constants';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-assists-jira-sts',
    templateUrl: './assists-jira-sts.component.html',
    styleUrls: ['./assists-jira-sts.component.scss'],
})
export class AssistsJiraStsComponent implements OnInit, AfterViewInit {
    @ViewChild('sentinel', { static: false })
    sentinel!: ElementRef;

    //#region Globals
    userRoles = USER_ROLES;

    selectedPanel: string = '';
    @Input() selectedPanelIndex: number = 0;

    issues: Array<JiraIssuesListRes> = [];

    private observer!: IntersectionObserver;
    pageSize: number = 50;
    isLastPage: boolean = false;
    nextPageToken: string = null;
    orgRequestsColumns: string[] = ['key', 'title', 'reporterName', 'created', 'assistAllocationDate', 'assistAmount', 'status'];
    displayedData: MatTableDataSource<JiraIssuesListRes> = new MatTableDataSource();
    isLoading: boolean = true;
    loadSessionId: number = 0;
    // Keep track of if the initial fetch of issues is complete
    initialFetchComplete: boolean = false;
    descriptionEditor = ClassicEditor;
    descriptionTextHTML: string;
    navigation: FuseNavigationItem[];
    userDetails: ExposedReadUserDetailsRes = null;

    descriptionTooltip = 'Tilgreindu fjárhæð reiknings og aðrar viðbótarupplýsingar sem þú telur að sé mikilvægt að komi fram';
    issueTypeId = '10029';

    issueProjectKey = 'STS';

    systemDropDown: Array<DropDownItem> = [];
    originDropDown: Array<DropDownItem> = [];
    accessTypeDropDown: Array<DropDownItem> = [];
    approverDropDown: Array<DropDownItem> = [];

    files: File[] = [];

    @ViewChild('requestForm') ngRequestForm: NgForm;
    @ViewChild('searchForm') ngSearchForm: NgForm;

    form: UntypedFormGroup;
    searchForm: UntypedFormGroup;
    submitted = false;
    formEnabled = true;

    assistTypes: Array<DropDownItem> = [
        {
            value: 'likamsraekt',
            viewValue: 'Líkamsrækt',
        },
        {
            value: 'sjonvernd',
            viewValue: 'Sjónvernd starfsmanna',
        },
        {
            value: 'simastyrkur',
            viewValue: 'Símastyrkur',
        },
    ];

    grantsList: ExposedListDWAssistsJiraStsRes[];
    fitnessGrantAmount: string;
    visionGrantAmount: string;
    phoneGrantAmount: string;
    year: string;

    //#endregion

    //#region Helper functions

    onTypeChange(value) {
        this.form.get('type').setValue(value);
        if (value == 'likamsraekt') {
            this.form.get('summary').setValue('Líkamsrækt');
        } else if (value == 'sjonvernd') {
            this.form.get('summary').setValue('Sjónvernd starfsmanna - Gleraugnastyrkur');
            // Gleraugnastyrkur is set as default
            this.form.get('typeSight').setValue('gleraugu');
        } else if (value == 'simastyrkur') {
            this.form.get('summary').setValue('Símastyrkur');
        }
    }

    onTypeSightChange(value) {
        if (value == 'gleraugu') {
            this.form.get('summary').setValue('Sjónvernd starfsmanna - Gleraugnastyrkur');
            this.form.get('typeSight').setValue('gleraugu');
        } else if (value == 'laser') {
            this.form.get('summary').setValue('Sjónvernd starfsmanna - Laseraðgerð');
            this.form.get('typeSight').setValue('laser');
        }
    }

    findCorrectTabBasedOnUrl() {
        try {
            this.selectedPanel = window.location.href.split('/').pop();
        } catch { }
    }

    initPanelBasedOnUrl() {
        if (this.selectedPanel == 'minar-beidnir') {
            this.selectedPanelIndex = 1;
        } else {
            this.selectedPanel = 'senda-beidni';
            this.selectedPanelIndex = 0;
        }
    }

    tabChanged(event: MatTabChangeEvent) {
        this.resetList();
        if (event.index == 0) {
            this.location.go('/atvik-og-beidnir/styrkir/senda-beidni');
        } else if (event.index == 1) {
            this.location.go('/atvik-og-beidnir/styrkir/minar-beidnir');
            this.fetchIssues();
        }
    }

    public descriptionOnChange({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.descriptionTextHTML = editor.getData();
        this.form.get('description').setValue(editor.getData());
    }

    formatDate(date) {
        return formatDate(date, false);
    }

    public onEditorReady(editor: any) {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => new InlineBase64UploadAdapter(loader);
    }

    public editorConfig: any = {
        alignment: { options: ['left', 'right', 'center', 'justify'] },
        toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', 'link', 'imageUpload', '|', 'undo', 'redo'],
        image: { toolbar: ['toggleImageCaption', 'imageTextAlternative'] },
    };

    //#endregion

    //#region Init

    constructor(
        private translationService: TranslationService,
        private dialog: MatDialog,
        private location: Location,
        public jiraService: JiraService,
        public loaderService: LoaderService,
        private alertsService: AlertsService,
        private _formBuilder: UntypedFormBuilder,
        public lsrauthService: LsrauthService,
        private htmlToAdf: HtmlToAdfService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
        this.htmlToAdf.preload();
    }

    ngOnInit(): void {
        this.lsrauthService.fetchUserDetails(true).subscribe((userDetails) => {
            this.findCorrectTabBasedOnUrl();
            this.initPanelBasedOnUrl();
            this.fetchGrantsDetails();
            this.initForm();
        });
    }

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (this.nextPageToken && !this.isLoading) {
                        this.observer.unobserve(entry.target);
                        this.fetchIssues();
                    } else if (this.isLastPage) {
                        this.observer.disconnect();
                    }
                }
            });
        });
    }

    resetObserver() {
        if (!this.observer || !this.sentinel?.nativeElement) return;
        this.observer.disconnect();
        this.observer.observe(this.sentinel.nativeElement);
    }

    private mergeByKey(existing: any[], incoming: any[]) {
        const seen = new Set(existing.map((i) => i.key));
        const merged = [...existing];
        for (const item of incoming) {
            if (!seen.has(item.key)) {
                merged.push(item);
                seen.add(item.key);
            }
        }
        return merged;
    }

    // Map helper to prevent duplicating the mapping code
    private mapIssues(res: any) {
        return (res.issues ?? []).map((issue) => ({
            created: issue.fields.created,
            key: issue.key,
            url: 'https://lsr.atlassian.net/servicedesk/customer/portal/3/' + issue.key,
            title: issue.fields.summary,
            reporterName: issue.fields.reporter.displayName,
            status: issue.fields.status.name,
            eaDate: null,
            ownerName: null,
            implementerName: null,
            assistAmount: null,
            assistAllocationDate: null,
        }));
    }

    initForm() {
        this.submitted = false;
        this.formEnabled = true;

        this.descriptionTextHTML = '';
        this.files = [];
        this.descriptionEditor = ClassicEditor;

        this.form = this._formBuilder.group({
            type: new UntypedFormControl('', [Validators.required]),
            typeSight: new UntypedFormControl(''),
            summary: new UntypedFormControl(''),
            description: new UntypedFormControl(''),
        });
        this.searchForm = this._formBuilder.group({
            ssn: new UntypedFormControl('', [Validators.required]),
            searchInput: new UntypedFormControl(''),
        });
    }

    fetchGrantsDetails() {
        this.jiraService.getGrantsEmployees().subscribe({
            next: (res) => {
                this.year = res.find(x => x.grantsName == GrantsTypes.Year)?.grantsValue || null;
                this.fitnessGrantAmount = this.formatValue(res.find(x => x.grantsName == GrantsTypes.Fitness)?.grantsValue) || null;
                this.visionGrantAmount = this.formatValue(res.find(x => x.grantsName == GrantsTypes.Vision)?.grantsValue) || null;
                this.phoneGrantAmount = this.formatValue(res.find(x => x.grantsName == GrantsTypes.Phone)?.grantsValue) || null;
            }
        })
    }

    formatValue(value: string): string {
        return new Intl.NumberFormat('de-DE').format(Number(value));
    }

    //#endregion

    //#region Table fetch data, sort and filter

    searchFilter(filterValue: string) {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.displayedData.filter = filterValue;
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'key':
                    return sortCompare(a.key, b.key, isAsc);
                case 'title':
                    return sortCompare(a.title, b.title, isAsc);
                case 'reporterName':
                    return sortCompare(a.reporterName, b.reporterName, isAsc);
                case 'created':
                    return sortCompare(a.created, b.created, isAsc);
                case 'status':
                    return sortCompare(a.status, b.status, isAsc);
                case 'assistAmount':
                    return sortCompare(a.assistAmount, b.assistAmount, isAsc);
                case 'assistAllocationDate':
                    return sortCompare(a.assistAllocationDate, b.assistAllocationDate, isAsc);
                default:
                    return 0;
            }
        });
    }

    // Empty the list and reset pagination
    resetList() {
        this.issues = [];
        this.nextPageToken = null;
        this.isLastPage = false;
        this.displayedData.data = this.issues;
        this.initialFetchComplete = false;
    }

    fetchIssues() {
        this.isLoading = true;
        const currentSession = this.loadSessionId;
        const req: ExposedListJiraIssuesReq = {
            NextPageToken: this.nextPageToken,
            PageSize: this.pageSize,
            Project: this.issueProjectKey,
            Ssn: this.searchForm.get('ssn').value == '' ? null : this.searchForm.get('ssn').value,
        };

        this.jiraService.listIssues(req).subscribe({
            next: (res) => {
                this.isLoading = false;

                if (currentSession !== this.loadSessionId) return;

                const mapped = this.mapIssues(res);
                this.issues = this.mergeByKey(this.issues, mapped);

                this.displayedData.data = this.issues;
                this.isLastPage = res.isLast;
                this.nextPageToken = res.nextPageToken;
                this.initialFetchComplete = true;

                this.resetObserver();
            },
            error: () => {
                this.isLoading = false;

                this.alertsService.changealertHeader('Villa við að ná í lista');
                this.alertsService.changeAlertErrorMessage('Reyndu aftur síðar.');
                this.alertsService.changeAlertErrorShow(true);
            },
        });
    }

    optionSelected = (option: ExposedListPublicRegistryPersonsRes) => {
        if (option) {
            this.searchForm.get('ssn').setValue(option.ssn);
            this.resetList();
            this.fetchIssues();
        }
    };

    //#endregion

    //#region Attachements

    onSelect(event) {
        this.files.push(...event.addedFiles);
    }

    onRemove(event) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    //#endregion

    //#region Submit or revert changes

    async submit() {
        this.form.markAllAsTouched();
        this.submitted = true;
        if (this.form.invalid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                message: this.translationService.translate('text_confirm_send_request'),
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe(async (confirm: boolean) => {
            if (!confirm) {
                return;
            }

            this.formEnabled = false;
            this.form.disable();

            try {
                const html = this.form.get('description')!.value ?? '';
                const adfJson = await this.htmlToAdf.convert(html);
                const description: JiraCreateIssueFieldsDescriptionDTOs = JSON.parse(adfJson);
                const createUtReq: JiraCreateIssueDTOReq = {
                    fields: {
                        description: description,
                        issueType: {
                            id: this.issueTypeId,
                        },
                        project: {
                            key: this.issueProjectKey,
                        },
                        reporter: null,
                        summary: this.form.get('summary').value,
                        customfield_10010: null,
                        customfield_10066: null,
                        customfield_10067: null,
                        customfield_10068: null,
                        customfield_10111: null,
                        customfield_10069: null,
                        customfield_10075: null,
                        customfield_10098: null,
                        customfield_10099: null,
                        customfield_10078: null,
                        customfield_10079: null,
                        customfield_10081: null,
                        customfield_10085: null,
                    },
                };

                this.jiraService.createSTS(createUtReq, this.files).subscribe((issueKey) => {
                    if (issueKey) {
                        this.initForm();
                        this.selectedPanelIndex = 1;
                        this.resetList();
                        this.loadSessionId++;
                        const currentSession = this.loadSessionId;

                        const req: ExposedListJiraIssuesReq = {
                            NextPageToken: null,
                            PageSize: this.pageSize,
                            Project: this.issueProjectKey,
                            Ssn: this.searchForm.get('ssn').value == '' ? null : this.searchForm.get('ssn').value,
                            EnsureKey: issueKey,
                        };
                        this.isLoading = true;
                        this.jiraService
                            .listIssues(req)
                            .pipe(finalize(() => (this.isLoading = false)))
                            .subscribe({
                                next: (res) => {
                                    if (currentSession !== this.loadSessionId) return;

                                    this.issues = this.mapIssues(res);

                                    this.displayedData.data = this.issues;
                                    this.isLastPage = res.isLast;
                                    this.nextPageToken = res.nextPageToken ?? null;
                                    this.initialFetchComplete = true;

                                    this.resetObserver();
                                },
                                error: () => {
                                    this.alertsService.changealertHeader('Villa við að ná í lista');
                                    this.alertsService.changeAlertErrorMessage('Reyndu aftur síðar.');
                                    this.alertsService.changeAlertErrorShow(true);
                                },
                            });
                    } else {
                        this.form.enable();
                        this.form.markAsPristine();
                        this.initForm();
                    }
                });
            } catch (e) {
                this.alertsService.changealertHeader('Villa við breytingu lýsingar');
                this.alertsService.changeAlertErrorMessage('Tókst ekki að breyta yfir í ADF.');
                this.alertsService.changeAlertErrorShow(true);
                this.submitted = false;
                this.formEnabled = true;
                this.form.enable();
            }
        });
    }

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.form.reset();
            this.initForm();
        }
    }

    //#endregion
}
